import React, { Fragment, ReactNode } from 'react';
import { Button, Dialog, Loading } from '@/components';
import { Col, Container, Row } from 'reactstrap';
import { SectorProductConfigSectorContent } from '@/features/registerEvent/components/SectorProductConfigSectorSreen/components/SectorTicketConfigSectorContent';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { CustomTable } from '@/components/Table';
import { ReactComponent as Pen } from '@/assets/images/svg/pen.svg';
import { ReactComponent as Trash } from '@/assets/images/svg/lixeira.svg';
import { ReactComponent as ItemConfig } from '@/assets/images/svg/ItemConfig.svg';
import { ActionProps } from '@/components/Dialog';
import SectorIcon from '@/assets/images/svg/Sector';
import { States } from '@/helpers/common/states';
import Section from '@/model/Section';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import ProductSectionEvent from '@/model/SectionProductEvent';
import { columnsSectors } from './table';
import { SectorConfigContent } from '../../components/SectorConfigContent';
import { NameFiles, SectionGroup, ShouldShowModal } from '../../types';

interface StateProps {
  state: States;
  title: string | ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  sectionGroups: SectionGroup[];
  sections: Section[];
  formData: FormData;
  formErrors: FormErrors;
  formNameFiles: NameFiles;
  eventSection?: ProductSectionEvent;
  eventSections: ProductSectionEvent[];
}

interface DispatchProps {
  onToggle: () => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
    eventSection,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | ReactNode;
    eventSection?: ProductSectionEvent;
  }) => void;
  onSelectAllSubGroups: (categoryId: string, subCategoryId: string, checked: boolean) => void;
  onCheckProduct: (categoryId: string, subCategoryId: string, productId: string) => void;
  onCheckCombo: (categoryId: string, subCategoryId: string, comboId: string) => void;
  onChangeFormInput: OnChangeFormInput;
  onChangeFormFileInput: (inputName: string) => (file: File | undefined) => void;
  onGetAllSections: () => void;
  onCancelEdit: () => void;
  isFormValid: () => boolean;
  onSave: () => void;
  onGet: (eventSection: ProductSectionEvent) => void;
  onShowModalDelete: (eventSection: ProductSectionEvent) => void;
  onReturnTab: () => void;
  onNextTab: () => void;
}

type Props = StateProps & DispatchProps;

export const SectorProductConfigSectorContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  onToggle,
  onShouldShowModal,
  sectionGroups,
  onSelectAllSubGroups,
  onCheckProduct,
  onCheckCombo,
  sections,
  formData,
  formErrors,
  onChangeFormInput,
  onChangeFormFileInput,
  formNameFiles,
  onGetAllSections,
  onCancelEdit,
  shouldShowModal,
  eventSection,
  isFormValid,
  onSave,
  eventSections,
  onGet,
  onShowModalDelete,
  onReturnTab,
  onNextTab,
}) => {
  const titleRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (eventSection) {
      titleRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [eventSection]);

  const renderActionDialogToCancel: ActionProps = {
    title: 'Cancelar',
    onClick: (): void => {
      onCancelEdit();
      onToggle();
    },
    theme: 'noneBorder',
  };

  const countProducts = (data: ProductSectionEvent): number => {
    let value = 0;
    if (data.sectionGroup && data.sectionGroup.length > 0) {
      data.sectionGroup.forEach(group => {
        if (group.subGroups && group.subGroups.length > 0) {
          group.subGroups.forEach(subGroup => {
            if (subGroup.products && subGroup.products.length > 0) {
              value += subGroup.products.length;
            }
          });
        }
      });
    }
    return value;
  };

  const countCombos = (data: ProductSectionEvent): number => {
    let value = 0;
    if (data.sectionGroup && data.sectionGroup.length > 0) {
      data.sectionGroup.forEach(group => {
        if (group.subGroups && group.subGroups.length > 0) {
          group.subGroups.forEach(subGroup => {
            if (subGroup.combos && subGroup.combos.length > 0) {
              value += subGroup.combos.length;
            }
          });
        }
      });
    }
    return value;
  };
  return (
    <Fragment>
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        isContentWithCard
        actions={[
          {
            [ShouldShowModal.configProduct]: renderActionDialogToCancel,
          }[shouldShowModal],
          {
            [ShouldShowModal.configProduct]: {
              title: 'Salvar',
              onClick: (): void => onSave(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.configProduct]: (
              <SectorConfigContent
                sectionGroups={sectionGroups}
                onSelectAllSubGroups={onSelectAllSubGroups}
                onCheckProduct={onCheckProduct}
                onCheckCombo={onCheckCombo}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Loading isVisible={state === States.loading} />
      <Container className="mainContainer" fluid={true}>
        <h6 ref={titleRef} className="mb-4">
          {eventSection ? `Editando ${eventSection.sectionNome}` : 'Configurando setores'}
        </h6>
        <div className="card-ligth-color mb-5">
          <Row>
            <Col>
              <div className="container-event">
                <SectorProductConfigSectorContent
                  sections={sections}
                  formData={formData}
                  formErrors={formErrors}
                  onChangeFormInput={onChangeFormInput}
                  onChangeFormFileInput={onChangeFormFileInput}
                  formNameFiles={formNameFiles}
                  onGetAllSections={onGetAllSections}
                />
              </div>
              <div className="d-flex justify-content-end">
                <div
                  className="cursor-pointer mr-3"
                  onClick={() => {
                    onCancelEdit();
                  }}
                >
                  {eventSection ? 'Cancelar' : null}
                </div>
                {eventSection ? (
                  <div
                    className="link-green"
                    onClick={(): void => {
                      if (isFormValid()) {
                        onShouldShowModal({
                          value: ShouldShowModal.configProduct,
                          newTitleModal: 'Configurações do setor',
                          eventSection,
                        });
                      }
                    }}
                  >
                    Salvar edição
                  </div>
                ) : (
                  <div
                    className="link-green"
                    onClick={(): void => {
                      if (isFormValid()) {
                        onShouldShowModal({
                          value: ShouldShowModal.configProduct,
                          newTitleModal: 'Configurações do setor',
                          eventSection,
                        });
                      }
                    }}
                  >
                    + inserir setor
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
        <SuperCollapse
          title={`Setores inseridos`}
          content={
            eventSections && eventSections.length > 0
              ? eventSections.map((data, index) => (
                  <React.Fragment key={index}>
                    {index > 0 ? <hr style={{ margin: '15px -30px 30px -30px' }} /> : null}
                    <div
                      className={`${
                        eventSection && eventSection.sectionId === data.sectionId
                          ? 'disabled-content'
                          : null
                      }`}
                      key={index}
                    >
                      <div className="d-flex justify-content-between">
                        <div className="mb-3">
                          <span className="secondary-table-title">Setor #{index + 1}</span>
                          <span className="secondary-table-title font-weight-bold">
                            <b> ·</b> {data.sectionNome}
                          </span>
                        </div>
                        <div className={`${eventSection ? 'disabled-content' : null}`}>
                          <div className="d-flex align-items-center">
                            <div className="ml-4">
                              <ItemConfig
                                className="mr-4 svg-icon action-icon"
                                onClick={(): void => {
                                  onShouldShowModal({
                                    value: ShouldShowModal.configProduct,
                                    newTitleModal: 'Configurações do setor',
                                    eventSection: data,
                                  });
                                }}
                              />
                              <Pen
                                className="mr-4 svg-icon action-icon"
                                onClick={(): void => onGet(data)}
                              />
                              <Trash
                                className="svg-icon svg-icon-trash action-icon"
                                onClick={() => {
                                  onShowModalDelete(data);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <CustomTable
                        numberRowsPerPage={0}
                        progressPending={false}
                        columns={columnsSectors}
                        data={[
                          {
                            id: data.sectionId,
                            products: countProducts(data),
                            combos: countCombos(data),
                          },
                        ]}
                        theme="secondaryWithoutBorder"
                      />
                    </div>
                  </React.Fragment>
                ))
              : 'Nenhum setor cadastrado. Aqui será exibida uma lista dos setores cadastrados'
          }
          count={eventSections.length}
          leftIcon={SectorIcon()}
        />
        <div className="d-flex justify-content-end">
          <Button title="Voltar etapa" theme="noneBorder" onClick={() => onReturnTab()} />
          <Button
            title="Próxima etapa"
            theme="outlineDark"
            className="ml-3"
            onClick={async () => {
              onNextTab();
            }}
          />
        </div>
      </Container>
    </Fragment>
  );
};
